import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, FormRadioGroup, InPageAlert, FormGroup, Input } from '@snsw/react-component-library';
import { PlantDesignDetailsPage } from 'plantDesign/constants/plantDesignDetailsPage';
import { PlantDesignRefData } from 'plantDesign/constants/PlantDesignRefData';
import { mapRefDataPickList, sanitizeHtml } from 'shared/utils/Helpers';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import styles from '../PlantDesignDetails.module.scss';

export const PressureVesselDesignDetails = React.forwardRef((props, ref) => {
  const {
    numberOfChambers: savedNumberOfChambers = '',
    hazardLevel: savedHazardLevel = '',
    designPressure: savedDesignPressure = '',
    minVolume: savedMinVolume = '',
    maxVolume: savedMaxVolume = '',
    minDesignTemp: savedMinDesignTemp = '',
    maxDesignTemp: savedMaxDesignTemp = '',
  } = props.value || {};

  const [numberOfChambers, setNumberOfChambers] = useState(savedNumberOfChambers);
  const [hazardLevel, setHazardLevel] = useState(savedHazardLevel);
  const [designPressure, setDesignPressure] = useState(savedDesignPressure);
  const [minVolume, setMinVolume] = useState(savedMinVolume);
  const [maxVolume, setMaxVolume] = useState(savedMaxVolume);
  const [minDesignTemp, setMinDesignTemp] = useState(savedMinDesignTemp);
  const [maxDesignTemp, setMaxDesignTemp] = useState(savedMaxDesignTemp);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const REF_CONSTANTS = PlantDesignRefData.pressureVessel;
  const PAGE_CONSTANTS = PlantDesignDetailsPage.pressureVessel;

  const hazardLevelOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.hazardLevelOptions]);
  const numberOfChambersOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.numberOfChambersOptions]);

  const validators = [
    {
      fieldName: 'numberOfChambers',
      fieldValue: numberOfChambers,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'hazardLevel',
      fieldValue: hazardLevel,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'minVolume',
      fieldValue: minVolume,
      isValid: (v) => isEmpty(v) || isNumeric(v),
      errorMessage: () => 'Please enter only numeric value',
    },
    {
      fieldName: 'maxVolume',
      fieldValue: maxVolume,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'designPressure',
      fieldValue: designPressure,
      isValid: (v) => isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'minDesignTemp',
      fieldValue: minDesignTemp,
      isValid: (v) => isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxDesignTemp',
      fieldValue: maxDesignTemp,
      isValid: (v) => isNumeric(v),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        let isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);
        setShowErrors(!isCurrentComponentValid);

        return isCurrentComponentValid;
      },
    };
  }

  useEffect(() => {
    let details = {
      numberOfChambers,
      hazardLevel,
      designPressure,
      minVolume,
      maxVolume,
      minDesignTemp,
      maxDesignTemp,
    };

    if (props.onChange) props.onChange(details);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [numberOfChambers, hazardLevel, designPressure, minVolume, maxVolume, minDesignTemp, maxDesignTemp]);

  useEffect(() => {
    if (props.onErrorChange) props.onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <FormRadioGroup
        id="numberOfChambers"
        name="numberOfChambers"
        legend={PAGE_CONSTANTS.numberOfChambers.label}
        options={numberOfChambersOptions}
        vertical={true}
        onChange={setNumberOfChambers}
        value={numberOfChambers}
        errorMessage={errors?.numberOfChambers?.errorMessage}
        hasError={showErrors && errors?.numberOfChambers?.invalid}
        margin={{ top: 'xl' }}
      />

      <InPageAlert variant="info" title="Help:" className={styles.marginTop2rem}>
        Please provide the chamber details for the most hazardous chamber in the multi-chamber pressure vessel.
      </InPageAlert>

      <FormRadioGroup
        id="hazardLevel"
        name="hazardLevel"
        legend={PAGE_CONSTANTS.hazardLevel.label}
        helpMessage={PAGE_CONSTANTS.hazardLevel.helpMessage}
        options={hazardLevelOptions}
        vertical={true}
        onChange={setHazardLevel}
        value={hazardLevel}
        errorMessage={errors?.hazardLevel?.errorMessage}
        hasError={showErrors && errors?.hazardLevel?.invalid}
        margin={{ top: 'xl' }}
      />

      <FormGroup
        id="minVolume"
        label={PAGE_CONSTANTS.minVolume.label}
        isRequired
        margin={{ top: 'md' }}
        errorMessage={errors?.minVolume?.errorMessage}
        hasError={showErrors && errors?.minVolume?.invalid}
        className={styles.inputWithSuffix}
      >
        <Input
          suffix={PAGE_CONSTANTS.minVolume.suffix}
          onChange={(e) => {
            setMinVolume(e.target.value);
          }}
          value={minVolume}
        />
      </FormGroup>

      <FormGroup
        id="maxVolume"
        label={PAGE_CONSTANTS.maxVolume.label}
        isRequired
        margin={{ top: 'md' }}
        errorMessage={errors?.maxVolume?.errorMessage}
        hasError={showErrors && errors?.maxVolume?.invalid}
        className={styles.inputWithSuffix}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxVolume.suffix}
          onChange={(e) => {
            setMaxVolume(e.target.value);
          }}
          value={maxVolume}
        />
      </FormGroup>

      <FormGroup
        id="designPressure"
        label={PAGE_CONSTANTS.designPressure.label}
        helpMessage="kPAG = Kilo Pascal. Also referred to as KpA"
        errorMessage={errors?.designPressure?.errorMessage}
        hasError={showErrors && errors?.designPressure?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'md' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.designPressure.suffix}
          onChange={(e) => setDesignPressure(e.target.value)}
          value={designPressure}
        />
      </FormGroup>

      <Row>
        <Col span={4}>
          <FormGroup
            id="minDesignTemp"
            label={PAGE_CONSTANTS.designTemp.label}
            errorMessage={errors?.minDesignTemp?.errorMessage}
            hasError={showErrors && errors?.minDesignTemp?.invalid}
            className={styles.inputWithSuffix}
            margin={{ top: 'md' }}
          >
            <Input
              suffix={sanitizeHtml(PAGE_CONSTANTS.minDesignTemp.suffix)}
              onChange={(e) => setMinDesignTemp(e.target.value)}
              value={minDesignTemp}
            />
          </FormGroup>
        </Col>
        <Col span={4}>
          <FormGroup
            id="maxDesignTemp"
            label="&nbsp;"
            errorMessage={errors?.maxDesignTemp?.errorMessage}
            hasError={showErrors && errors?.maxDesignTemp?.invalid}
            className={styles.inputWithSuffix}
            margin={{ top: 'md' }}
          >
            <Input
              suffix={sanitizeHtml(PAGE_CONSTANTS.maxDesignTemp.suffix)}
              onChange={(e) => setMaxDesignTemp(e.target.value)}
              value={maxDesignTemp}
            />
          </FormGroup>
        </Col>
      </Row>
    </>
  );
});

PressureVesselDesignDetails.displayName = 'PressureVesselDetails';
PressureVesselDesignDetails.propTypes = {
  value: PropTypes.object,
  refData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onErrorChange: PropTypes.func.isRequired,
};
